import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Languages, Search, X, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { LANGUAGES } from "../i18n/languages";

const GROUPS = ["Primary", "India", "World"];

export default function TranslateButton({ variant = "nav" }) {
  const { lang, setLang, t, meta } = useLang();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return LANGUAGES;
    return LANGUAGES.filter(
      (l) => l.label.toLowerCase().includes(q) || l.native.toLowerCase().includes(q) || l.code.includes(q)
    );
  }, [query]);

  const choose = (code) => {
    setLang(code);
    setOpen(false);
    setQuery("");
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={
          variant === "floating"
            ? "glass-strong flex h-12 w-12 items-center justify-center rounded-full text-tara-sindoor hover:scale-105 transition-transform"
            : "btn-ghost-gold flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold"
        }
        data-testid={variant === "floating" ? "translate-fab" : "translate-btn"}
        aria-label={t("translate_title")}
        title={t("translate_title")}
      >
        <Languages className="h-4 w-4" />
        {variant !== "floating" && <span>{meta.short}</span>}
      </button>

      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[120] flex items-start justify-center p-4 sm:p-8 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-[#14262A]/55 backdrop-blur-md"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="glass-strong relative w-full max-w-2xl rounded-[1.8rem] p-6 sm:p-8 mt-10"
              data-testid="translate-modal"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute right-5 top-5 text-tara-muted hover:text-tara-sindoor transition-colors"
                data-testid="translate-close"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              <span className="badge-gold inline-flex items-center gap-2 rounded-full px-3.5 py-1 text-[10px] font-serif uppercase tracking-[0.24em] mb-4">
                <Languages className="h-3 w-3" /> {LANGUAGES.length} languages
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-tara-ink mb-2">{t("translate_title")}</h2>
              <p className="text-sm text-tara-body mb-6">{t("translate_sub")}</p>

              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-tara-muted" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("translate_search")}
                  className="w-full rounded-full border border-tara-gold/25 bg-white/80 py-3 pl-11 pr-4 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-gold/60"
                  data-testid="translate-search-input"
                />
              </div>

              <div className="max-h-[50vh] overflow-y-auto pr-1 space-y-6">
                {GROUPS.map((group) => {
                  const items = filtered.filter((l) => l.group === group);
                  if (!items.length) return null;
                  return (
                    <div key={group}>
                      <p className="text-[10px] font-serif uppercase tracking-[0.24em] text-tara-goldDeep mb-3">{group}</p>
                      <div className="grid gap-2.5 sm:grid-cols-2">
                        {items.map((l) => (
                          <button
                            key={l.code}
                            onClick={() => choose(l.code)}
                            className={`group flex items-center justify-between rounded-2xl border px-4 py-3 text-left transition-all hover:-translate-y-0.5 ${
                              lang === l.code
                                ? "border-tara-gold/60 bg-gradient-to-r from-tara-gold/15 to-tara-vermilion/10"
                                : "border-tara-gold/20 bg-white/70 hover:border-tara-gold/45 hover:bg-white"
                            }`}
                            data-testid={`translate-option-${l.code}`}
                          >
                            <span>
                              <span className="block font-display text-lg text-tara-ink leading-tight">{l.native}</span>
                              <span className="block text-[11px] uppercase tracking-[0.14em] text-tara-muted">{l.label}</span>
                            </span>
                            {lang === l.code ? (
                              <Check className="h-4 w-4 text-tara-vermilion" />
                            ) : (
                              <span className="text-[10px] font-bold text-tara-gold/70">{l.short}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {filtered.length === 0 && (
                  <p className="text-sm text-tara-muted text-center py-8">{t("no_results")}</p>
                )}
              </div>

              <p className="text-[11px] text-tara-muted mt-6 leading-relaxed">{t("translate_note")}</p>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
